import CardType from './CardType';
import CardParameter from './CardParameter';
import CardDetailInformation from './CardDetailInformation';
import AdditionalInformation from './AdditionalInformation';
import { Button, Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useState } from 'react';
import { confirmDialog, successDialog, Text, useText, warningDialog } from '@src/components';
import { cardOrdersDelete, cardOrdersUpdate, reset } from '@src/store/slice/cardOrderSlice';
import {
  cardOrderDetailsGelAll,
  resetDetail,
  cardOrderDetailsGetAllGsms,
} from '@src/store/slice/cardOrderDetailtsSlice';
import '@src/styles/cardOrder/cardInformation.scss';
import { useHistory } from 'react-router-dom';
import { setDraftType } from '@src/store/slice/draftOrderSlice';
import { getMaximumLoadedAmount } from '@src/store/slice/orderLimitsSlice';
import dayjs from '@src/utils/day';
import { dataLayer } from '@src/utils/utils';

const CardInformation = () => {
  const { currentCardOrder, activeStep, cardOrderPopupDigital } = useSelector(
    (state) => state?.cardOrder,
  );
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const history = useHistory();
  const { cardList } = useSelector((state) => state?.cardOrderDetails);

  const [isExit, setIsExit] = useState(false);

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  useEffect(() => {
    dispatch(getMaximumLoadedAmount());

    if ([0, 1].includes(currentCardOrder.cardType)) {
      (async () => {
        await dispatch(cardOrderDetailsGelAll({ pageNumber: 1, pageSize: 10 }));
      })();
    }
  }, [currentCardOrder?.cardType, dispatch]);

  const handleOrderApproval = useCallback(async () => {
    if (
      Object.keys(detailForm.getFieldsValue()).length > 0 &&
      Object.keys(detailForm.getFieldValue()).length > 0
    ) {
      warningDialog({
        title: <Text t="attention" />,
        message: <Text t="incompleteTransaction" />,
      });
    } else {
      try {
        const additionalInformationValues = await form.validateFields();
        const cardActivationDate = additionalInformationValues.cardActivationDate
          ? additionalInformationValues.cardActivationDate
            ?.hour(23)
            .minute(59)
            .millisecond(0)
            .second(59)
            .format('YYYY-MM-DDTHH:mm:ssZ')
          : dayjs().hour(23).minute(59).millisecond(0).second(59).format('YYYY-MM-DDTHH:mm:ssZ');

        const currentGsms = await dispatch(cardOrderDetailsGetAllGsms()).unwrap();

        const body = {
          ...currentCardOrder,
          ...additionalInformationValues,
          orderStatus: 10,
          cardActivationDate,
          gsms: currentGsms,
        };

        if (body.cardType === 1) {
          body.printCompanyName = false;
          body.printNameSurname = false;
        }

        const response = await dispatch(cardOrdersUpdate(body));

        if (response?.payload?.data?.errorMessage) {
          warningDialog({
            title: <Text t="attention" />,
            message: response.payload.data.errorMessage,
          });
          return;
        }
        dataLayer(
          'kart_siparis_girisi',
          body?.cardType === 0 ? 'Fiziksel Kart' : 'Dijital Kart',
          cardOrderPopupDigital ? 'Pop up - Dijital Kart Seçin' : '',
        );
      } catch (e) { }
    }
  }, [currentCardOrder, dispatch, form, detailForm]);

  const submitDraftSave = useCallback(
    async (to) => {
      const additionalInformationValues = await form.getFieldsValue();
      const body = {
        ...currentCardOrder,
        ...additionalInformationValues,
        draft: true,
        orderStatus: 0,
        cardActivationDate: additionalInformationValues.cardActivationDate
          ? additionalInformationValues.cardActivationDate
            .hour(23)
            .minute(59)
            .millisecond(0)
            .second(59)
            .format('YYYY-MM-DDTHH:mm:ssZ')
          : dayjs().hour(23).minute(59).millisecond(0).second(59).format('YYYY-MM-DDTHH:mm:ssZ'),
      };
      const action = await dispatch(cardOrdersUpdate(body));
      if (cardOrdersUpdate.fulfilled.match(action)) {
        successDialog({
          title: <Text t="successfullySent" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
          onOk: async () => {
            await dispatch(setDraftType(1));
            await dispatch(reset());
            history.push(to);
          },
        });
      } else {
        setIsExit(false);
      }
    },
    [currentCardOrder, dispatch, form],
  );

  const handleOrderDraftSave = useCallback(async () => {
    confirmDialog({
      title: <Text t="attention" />,
      htmlContent: <Text t="orderDraftSaveConfirmMessage" />,
      onOk: async () => {
        setIsExit(true);
        submitDraftSave('/ticket-restaurant/draft-order');
      },
    });
  }, [submitDraftSave]);

  const handleOrderCancel = useCallback(async () => {
    const action = await dispatch(cardOrdersDelete());
    if (cardOrdersDelete.fulfilled.match(action)) {
      successDialog({
        title: <Text t="successfullySent" />,
        message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
      });
      await dispatch(resetDetail());
      window.scrollTo(0, 0);
    }
  }, [dispatch]);

  useEffect(() => {
    const unblock = history.block((location) => {
      if (isExit) {
        return true;
      }

      const control = cardList?.filter((recordItem) => recordItem?.id);

      if (activeStep === 0 && control.length > 0 && currentCardOrder.pendingApproval !== true) {
        confirmDialog({
          title: <Text t="attention" />,
          htmlContent: <Text t="myOrdersBeforeUnloadMessage" />,
          okText: <Text t="yes" />,
          cancelText: <Text t="no" />,
          closeIcon: false,
          onOk: async () => {
            setIsExit(true);
            await submitDraftSave(location.pathname);
            return true;
          },
          onCancel: async (e) => {
            if (e?.target?.className?.split(' ')?.includes('close-image')) return;
            await dispatch(cardOrdersDelete());
            history.push(location.pathname);
          },
        });

        unblock();
      } else {
        return true;
      }

      return false;
    });

    return unblock;
  }, [
    activeStep,
    cardList,
    cardList.length,
    detailForm,
    handleOrderCancel,
    history,
    submitDraftSave,
    currentCardOrder,
  ]);

  return (
    <div className="card-order-content order-input-form-page">
      <CardType form={detailForm} />

      {currentCardOrder.cardType === 0 && <CardParameter />}

      {[0, 1, 2].includes(currentCardOrder.cardType) && (
        <>
          <CardDetailInformation form={detailForm} />
          <AdditionalInformation form={form} />
        </>
      )}

      <div className="step-btn-container">
        <Button
          id="cardOrders-cardInformation-main-cancel-btn"
          className="cancel-btn"
          disabled={currentCardOrder?.id === 0}
          onClick={() => {
            return confirmDialog({
              title: <Text t="attention" />,
              htmlContent: <Text t="areYouSureYouWantToCancelThe" />,
              okText: <Text t="yes" />,
              cancelText: <Text t="no" />,
              onOk: async () => {
                return handleOrderCancel();
              },
              onCancel: async () => { },
            });
          }}
        >
          <Text t="cancelOrder" />
        </Button>
        <Button
          id="cardOrders-cardInformation-main-draft-btn"
          className="draft-btn"
          onClick={handleOrderDraftSave}
          disabled={currentCardOrder?.totalCount === 0}
        >
          <Text t="saveAsDraft" />
        </Button>
        <Button
          id="cardOrders-cardInformation-main-next-btn"
          className="next-btn"
          onClick={handleOrderApproval}
          disabled={currentCardOrder?.totalCount === 0}
        >
          <Text t="approveOrder" />
        </Button>
      </div>
    </div>
  );
};

export default CardInformation;
